// src/components/SessionsReportScreen.jsx
// Relatório de sessões premium.
// Rota: /reports/sessions
// Endpoints:
// GET /psy/report/sessions?id=ID_USUARIO
// GET /psy/insight/sessions?id=ID_USUARIO  (somente plano premium)
// UI isolada em .sess-* para não mexer em .dash-* global.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, AlertTriangle, BarChart3, CalendarDays, CheckCircle2, Edit3, Menu, Monitor, Search, SlidersHorizontal, Users,
  WalletCards, X, XCircle,
} from "lucide-react";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import PsyChartTooltip from "./PsyChartTooltip";
import { PSY_AXIS_TICK, PSY_CHART, PSY_GRID_PROPS } from "./PsyChartTheme";

import AppSidebar from "./AppSidebar";
import InsightsWidget from "./InsightsWidget";
import Logo from "../assets/logoClaro.webp";
import "../sass/pages/sessions-report.scss";
import "../sass/components/psy-charts.scss";
import { API_BASE as CONFIG_API_BASE } from "../config/apiBase";

const API_BASE = (
  CONFIG_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api-psyassist-production.up.railway.app"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
  PLAN: "plano",
};

const SESSION_FILTERS = [
  { value: "todas", label: "Todas" },
  { value: "online", label: "Online" },
  { value: "presencial", label: "Presencial" },
  { value: "cancelada", label: "Canceladas" },
];

const STATUS_FILTERS = [
  { value: "todos", label: "Todos os status" },
  { value: "concluida", label: "Concluídas" },
  { value: "cancelada", label: "Canceladas" },
];

const PAYMENT_FILTERS = [
  { value: "todos", label: "Todos os pagamentos" },
  { value: "true", label: "Pagas" },
  { value: "false", label: "Pendentes" },
];

const ORIGIN_FILTERS = [
  { value: "todos", label: "Todas as origens" },
  { value: "agendamento", label: "Agenda" },
  { value: "manual", label: "Manual" },
  { value: "retroativa", label: "Retroativa" },
];

function readStorage(keys = []) {
  for (const key of keys) {
    const value = sessionStorage.getItem(key) || localStorage.getItem(key);

    if (value && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
}

function saveAuthToken(token) {
  if (!token) return;

  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
}

function normalizeText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function safeNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function percentBR(value) {
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(safeNumber(value));
}

function brl(value) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(safeNumber(value));
}

function buildQuery(params = {}) {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (String(value).trim() === "") return;
    if (String(value).trim() === "todos") return;
    query.set(key, String(value));
  });

  return query.toString();
}

function parseDate(value) {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateBR(value) {
  const date = parseDate(value);

  if (!date) return "Não informado";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function formatDateTimeBR(value) {
  const date = parseDate(value);

  if (!date) return "Não informado";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getTimeBR(value) {
  const date = parseDate(value);

  if (!date) return "--:--";

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();

  if (!clean) return "";

  return clean.split(/\s+/)[0];
}

function getSessionType(raw = "") {
  const normalized = normalizeText(raw);

  if (normalized.includes("online")) return "online";
  if (normalized.includes("presencial")) return "presencial";
  if (normalized.includes("cancel")) return "cancelada";

  return normalized || "presencial";
}

function getTypeLabel(type = "") {
  const normalized = getSessionType(type);

  if (normalized === "online") return "Online";
  if (normalized === "cancelada") return "Cancelada";
  return "Presencial";
}

function getTypeIcon(type = "") {
  const normalized = getSessionType(type);
  if (normalized === "online") return Monitor;
  if (normalized === "cancelada") return XCircle;
  return Users;
}

function normalizeRecentSessions(data = []) {
  const rows = Array.isArray(data) ? data : [];

  return rows.slice(0, 60).map((item, index) => {
    const tipo = item?.tipo || item?.servico || item?.tipoSessao || "Presencial";
    const dataSessao = item?.data || item?.dataSessao || item?.dataHora || item?.createdAt || null;
    const cliente = item?.cliente || item?.clienteNome || item?.nomeCliente || "Paciente";

    return {
      id: item?._id || item?.id || `${cliente}-${dataSessao || index}`,
      cliente: String(cliente || "Paciente").trim(),
      data: dataSessao,
      tipo: getTypeLabel(tipo),
      tipoKey: getSessionType(tipo),
      status: String(item?.status || "").trim(),
      origem: String(item?.origem || "").trim(),
      pagamentoEfetuado: Boolean(item?.pagamentoEfetuado),
      formaPagamento: String(item?.formaPagamento || "").trim(),
      valor: safeNumber(item?.valor ?? item?.valorAgendamento ?? 0),
    };
  });
}

function normalizeEvolution(data = []) {
  const rows = Array.isArray(data) ? data : [];

  return rows
    .map((item, index) => ({
      label: item?.label || item?.semana || `S${safeNumber(item?.i ?? index) + 1}`,
      total: safeNumber(item?.total ?? item?.quantidade ?? item?.qtd ?? 0),
    }))
    .filter((item) => Number.isFinite(item.total));
}

function buildSearchText(session) {
  return normalizeText(
    [
      session.cliente,
      session.tipo,
      session.status,
      session.origem,
      session.pagamentoEfetuado ? "paga" : "pendente",
      formatDateBR(session.data),
      formatDateTimeBR(session.data),
    ]
      .filter(Boolean)
      .join(" ")
  );
}

async function readApiError(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const payload = await response.json();
      return payload?.message || payload?.error || JSON.stringify(payload);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function getSessionHealth(total, canceladas) {
  const totalNumber = safeNumber(total);
  const canceledNumber = safeNumber(canceladas);
  const cancelRate = totalNumber > 0 ? (canceledNumber / totalNumber) * 100 : 0;

  if (totalNumber <= 0) {
    return {
      label: "Sem dados",
      tone: "neutral",
      icon: "i",
      description: "Ainda não há sessões suficientes para leitura do período.",
      cancelRate,
    };
  }

  if (cancelRate >= 35) {
    return {
      label: "Atenção",
      tone: "bad",
      icon: AlertTriangle,
      description: "A taxa de cancelamento está alta para o período.",
      cancelRate,
    };
  }

  if (cancelRate >= 15) {
    return {
      label: "Monitorar",
      tone: "warning",
      icon: AlertTriangle,
      description: "Há cancelamentos relevantes. Vale acompanhar a agenda.",
      cancelRate,
    };
  }

  return {
    label: "Saudável",
    tone: "good",
    icon: CheckCircle2,
    description: "O volume de sessões está com boa estabilidade.",
    cancelRate,
  };
}

function KpiCard({ icon: Icon, label, value, description, tone = "blue" }) {
  return (
    <article className={`sess-kpi sess-kpi--${tone}`}>
      <div className="sess-kpi__icon" aria-hidden="true">
        <Icon size={19} strokeWidth={1.9} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{description}</small>
      </div>
    </article>
  );
}

function WeeklyChart({ data = [] }) {
  const rows = normalizeEvolution(data).slice(0, 12);

  if (!rows.length) {
    return (
      <div className="sess-chart-empty">
        <BarChart3 size={24} strokeWidth={1.8} />
        <strong>Sem evolução semanal</strong>
        <small>Quando houver dados suficientes, a evolução aparecerá aqui.</small>
      </div>
    );
  }

  return (
    <div className="sess-chart psy-chart-surface">
      <ResponsiveContainer width="100%" height={290}>
        <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: -18 }}>
          <CartesianGrid {...PSY_GRID_PROPS} />
          <XAxis dataKey="label" tick={PSY_AXIS_TICK} axisLine={false} tickLine={false} interval={0} />
          <YAxis allowDecimals={false} tick={PSY_AXIS_TICK} axisLine={false} tickLine={false} width={38} />
          <Tooltip content={<PsyChartTooltip />} cursor={{ fill: "rgba(85, 71, 232, 0.045)" }} />
          <Bar dataKey="total" name="Sessões" fill={PSY_CHART.indigo} radius={[7, 7, 2, 2]} maxBarSize={38} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function SessionsSkeleton() {
  return (
    <div className="sess-skeleton" aria-hidden="true">
      <div className="sess-skeleton__hero" />
      <div className="sess-skeleton__kpis">
        <span />
        <span />
        <span />
      </div>
      <div className="sess-skeleton__panel" />
      <div className="sess-skeleton__list">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function EmptyState({ title, message, onRetry, onBack, isError = false }) {
  return (
    <section className={`sess-state ${isError ? "sess-state--error" : ""}`}>
      <div className="sess-state__icon">{isError ? <AlertTriangle size={24} /> : <CalendarDays size={24} />}</div>
      <strong>{title}</strong>
      <span>{message}</span>

      <div className="sess-state__actions">
        <button className="sess-btn sess-btn--ghost-dark" type="button" onClick={onBack}>
          Voltar
        </button>

        <button className="sess-btn sess-btn--primary" type="button" onClick={onRetry}>
          Tentar novamente
        </button>
      </div>
    </section>
  );
}

export default function SessionsReportScreen() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [report, setReport] = useState(null);
  const [insights, setInsights] = useState([]);
  const [isPremium, setIsPremium] = useState(false);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const [filter, setFilter] = useState("todas");
  const [search, setSearch] = useState("");

  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [paymentFilter, setPaymentFilter] = useState("todos");
  const [originFilter, setOriginFilter] = useState("todos");
  const [statusModal, setStatusModal] = useState(null);
  const [statusReason, setStatusReason] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);

  const nomeUsuario = useMemo(() => readStorage([AUTH_KEYS.USER_NAME]) || "", []);
  const firstName = useMemo(() => getFirstName(nomeUsuario), [nomeUsuario]);

  const authOrRedirect = useCallback(() => {
    const token = readStorage([AUTH_KEYS.TOKEN, "token"]);
    const userId = readStorage([AUTH_KEYS.USER_ID, "id"]);
    const plano = String(readStorage([AUTH_KEYS.PLAN]) || "").toLowerCase();

    setIsPremium(plano === "premium");

    if (!token || !userId) {
      setError("Sessão expirada. Faça login novamente.");
      return null;
    }

    return { token, userId, isPremiumPlan: plano === "premium" };
  }, []);

  const loadData = useCallback(
    async ({ signal, silent = false } = {}) => {
      const auth = authOrRedirect();

      if (!auth) {
        setLoading(false);
        return;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const params = buildQuery({
          id: auth.userId,
          dataInicio,
          dataFim,
          status: filter === "cancelada" ? "cancelada" : statusFilter,
          tipoSessao:
            filter === "online"
              ? "Online"
              : filter === "presencial"
                ? "Presencial"
                : "todos",
          pagamentoEfetuado: paymentFilter,
          origem: originFilter,
        });

        const response = await fetch(
          `${API_BASE}/psy/report/sessions?${params}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${auth.token}`,
              "Content-Type": "application/json",
            },
            signal,
          }
        );

        saveAuthToken(response.headers.get("x-new-token"));

        if (response.status === 401 || response.status === 403) {
          setError("Sessão expirada. Faça login novamente.");
          setReport(null);
          setInsights([]);
          return;
        }

        if (!response.ok) {
          const message = await readApiError(response);
          setError(message || `Erro ao carregar relatório (${response.status}).`);
          setReport(null);
          setInsights([]);
          return;
        }

        const data = await response.json().catch(() => null);
        setReport(data || null);
        setLastUpdatedAt(new Date());

        if (auth.isPremiumPlan) {
          const insightResponse = await fetch(
            `${API_BASE}/psy/insight/sessions?id=${encodeURIComponent(auth.userId)}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${auth.token}`,
                "Content-Type": "application/json",
              },
              signal,
            }
          );

          saveAuthToken(insightResponse.headers.get("x-new-token"));

          if (insightResponse.ok) {
            const payload = await insightResponse.json().catch(() => ({}));
            const list = Array.isArray(payload?.insights) ? payload.insights : [];
            setInsights(list.map((item) => String(item)));
          } else {
            setInsights([]);
          }
        } else {
          setInsights([]);
        }
      } catch (err) {
        if (err?.name === "AbortError") return;

        console.error("Erro ao carregar relatório de sessões:", err);
        setError("Falha de comunicação com a API.");
        setReport(null);
        setInsights([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authOrRedirect, dataFim, dataInicio, filter, originFilter, paymentFilter, statusFilter]
  );

  useEffect(() => {
    const controller = new AbortController();
    loadData({ signal: controller.signal });

    return () => controller.abort();
  }, [loadData]);

  const resumo = report?.resumo || {};

  const online = safeNumber(resumo?.online);
  const presencial = safeNumber(resumo?.presencial);
  const canceladas = safeNumber(resumo?.canceladas);
  const concluidas = safeNumber(resumo?.concluidas);
  const pagas = safeNumber(resumo?.pagas);
  const pendentes = safeNumber(resumo?.pendentes);
  const valorRecebido = safeNumber(resumo?.valorRecebido);
  const valorPendente = safeNumber(resumo?.valorPendente);
  const taxaPagamento = safeNumber(resumo?.taxaPagamento);
  const total = safeNumber(resumo?.total, online + presencial + canceladas);

  const evolution = useMemo(
    () => normalizeEvolution(report?.evolucaoSemanal || []),
    [report]
  );

  const recentSessions = useMemo(
    () => normalizeRecentSessions(report?.listaDetalhada || report?.recentes || []),
    [report]
  );

  const filteredSessions = useMemo(() => {
    const query = normalizeText(search);

    return recentSessions.filter((session) => {
      const matchesFilter = filter === "todas" || session.tipoKey === filter;
      const matchesSearch = !query || buildSearchText(session).includes(query);

      return matchesFilter && matchesSearch;
    });
  }, [recentSessions, filter, search]);

  const filteredCounter = useMemo(() => {
    return filteredSessions.reduce(
      (acc, session) => {
        acc.total += 1;
        acc[session.tipoKey] = safeNumber(acc[session.tipoKey]) + 1;
        return acc;
      },
      { total: 0, online: 0, presencial: 0, cancelada: 0 }
    );
  }, [filteredSessions]);

  const health = useMemo(
    () => getSessionHealth(total, canceladas),
    [total, canceladas]
  );

  const correctSessionStatus = async () => {
    if (!statusModal?.id || statusSaving) return;
    const reason = String(statusReason || "").trim();
    if (reason.length < 5) {
      setError("Informe um motivo com pelo menos 5 caracteres para corrigir o status.");
      return;
    }
    const auth = authOrRedirect();
    if (!auth) return;
    const nextStatus = normalizeText(statusModal.status) === "cancelada" ? "concluida" : "cancelada";
    setStatusSaving(true);
    try {
      const response = await fetch(`${API_BASE}/psy/session/${encodeURIComponent(statusModal.id)}/corrigir-status`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${auth.token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status: nextStatus, motivo: reason }),
      });
      saveAuthToken(response.headers.get("x-new-token"));
      if (!response.ok) {
        const message = await readApiError(response);
        throw new Error(message || "Não foi possível corrigir o status.");
      }
      setStatusModal(null);
      setStatusReason("");
      await loadData({ silent: true });
    } catch (err) {
      setError(err?.message || "Não foi possível corrigir o status.");
    } finally {
      setStatusSaving(false);
    }
  };

  const reload = () => loadData({ silent: true });
  const clearManagementFilters = () => {
    setDataInicio("");
    setDataFim("");
    setStatusFilter("todos");
    setPaymentFilter("todos");
    setOriginFilter("todos");
    setFilter("todas");
    setSearch("");
  };
  const goBack = () => navigate("/reports");
  const goLogin = () => navigate("/login");
  const goSchedule = () => navigate("/schedule");
  const printReport = () => window.print();

  return (
    <div className="sess-page">
      <div className="sess-bg" aria-hidden="true">
        <span className="sess-bg__orb sess-bg__orb--one" />
        <span className="sess-bg__orb sess-bg__orb--two" />
        <span className="sess-bg__orb sess-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="sess-topbar">
        <button
          className="sess-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} />
        </button>

        <div className="sess-brand">
          <img src={Logo} alt="PsyAssist" className="sess-brand__logo" />
          <div className="sess-brand__text">
            <strong>Relatório de sessões</strong>
            <span>Resumo, evolução e recentes • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="sess-top-actions">
          <button
            className="sess-btn sess-btn--ghost sess-btn--desktop"
            type="button"
            onClick={goBack}
          >
            Voltar
          </button>

          <button
            className="sess-btn sess-btn--ghost"
            type="button"
            onClick={printReport}
            disabled={loading || Boolean(error) || !report}
          >
            Imprimir
          </button>

          <button
            className="sess-btn sess-btn--primary"
            type="button"
            onClick={reload}
            disabled={loading || refreshing}
          >
            {refreshing ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
      </header>

      <main className="sess-main">
        {loading ? (
          <SessionsSkeleton />
        ) : error ? (
          <EmptyState
            title="Não foi possível carregar as sessões."
            message={error}
            onRetry={String(error).includes("Sessão") ? goLogin : reload}
            onBack={goBack}
            isError
          />
        ) : !report ? (
          <EmptyState
            title="Nenhum dado encontrado."
            message="Ainda não há sessões suficientes para montar este relatório."
            onRetry={reload}
            onBack={goBack}
          />
        ) : (
          <>
            <section className="sess-hero">
              <div>
                <span className="sess-kicker">Visão clínica</span>
                <h1>Sessões</h1>
                <p>
                  {firstName ? `${firstName}, ` : ""}
                  acompanhe sessões online, presenciais, cancelamentos, evolução
                  semanal e atendimentos recentes.
                </p>
              </div>

              <aside className={`sess-hero-card sess-hero-card--${health.tone}`}>
                <div className="sess-hero-card__icon" aria-hidden="true">
                  {React.createElement(health.icon || Activity, { size: 20, strokeWidth: 1.9 })}
                </div>

                <div>
                  <small>Status do período</small>
                  <strong>{health.label}</strong>
                  <span>
                    Cancelamento: {percentBR(health.cancelRate)}% •{" "}
                    {health.description}
                  </span>
                </div>
              </aside>
            </section>

            <section className="sess-panel sess-management-filters">
              <div className="sess-panel__head">
                <div>
                  <span><SlidersHorizontal size={13} /> Filtros</span>
                  <h2>Refine a análise</h2>
                  <p>Filtre por período, status, pagamento e origem sem perder a visão premium do relatório.</p>
                </div>
              </div>

              <div className="sess-filter-grid">
                <label className="sess-filter-field">
                  <span>Data início</span>
                  <input
                    type="date"
                    value={dataInicio}
                    onChange={(event) => setDataInicio(event.target.value)}
                  />
                </label>

                <label className="sess-filter-field">
                  <span>Data fim</span>
                  <input
                    type="date"
                    value={dataFim}
                    onChange={(event) => setDataFim(event.target.value)}
                  />
                </label>

                <label className="sess-filter-field">
                  <span>Status</span>
                  <select
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    disabled={filter === "cancelada"}
                  >
                    {STATUS_FILTERS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="sess-filter-field">
                  <span>Pagamento</span>
                  <select
                    value={paymentFilter}
                    onChange={(event) => setPaymentFilter(event.target.value)}
                  >
                    {PAYMENT_FILTERS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="sess-filter-field">
                  <span>Origem</span>
                  <select
                    value={originFilter}
                    onChange={(event) => setOriginFilter(event.target.value)}
                  >
                    {ORIGIN_FILTERS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="sess-filter-actions">
                  <button className="sess-btn sess-btn--ghost-dark" type="button" onClick={clearManagementFilters}>
                    Limpar
                  </button>

                  <button className="sess-btn sess-btn--primary" type="button" onClick={reload} disabled={refreshing}>
                    Aplicar
                  </button>
                </div>
              </div>
            </section>

            <section className="sess-kpis">
              <KpiCard
                icon={Monitor}
                label="Online"
                value={online}
                description={`${percentBR(total > 0 ? (online / total) * 100 : 0)}% do total`}
                tone="blue"
              />

              <KpiCard
                icon={Users}
                label="Presencial"
                value={presencial}
                description={`${percentBR(total > 0 ? (presencial / total) * 100 : 0)}% do total`}
                tone="green"
              />

              <KpiCard
                icon={XCircle}
                label="Canceladas"
                value={canceladas}
                description={`${percentBR(total > 0 ? (canceladas / total) * 100 : 0)}% do total`}
                tone="red"
              />

              <KpiCard
                icon={CheckCircle2}
                label="Pagas"
                value={pagas}
                description={`${percentBR(taxaPagamento)}% das concluídas`}
                tone="green"
              />

              <KpiCard
                icon={WalletCards}
                label="Recebido"
                value={brl(valorRecebido)}
                description={`Pendente: ${brl(valorPendente)}`}
                tone="amber"
              />
            </section>

            <section className="sess-grid2">
              <div className="sess-panel">
                <div className="sess-panel__head">
                  <div>
                    <span>Evolução</span>
                    <h2>Evolução semanal</h2>
                    <p>Visualize o volume de sessões nas últimas semanas.</p>
                  </div>

                  {lastUpdatedAt ? (
                    <div className="sess-updated">
                      Atualizado às {getTimeBR(lastUpdatedAt)}
                    </div>
                  ) : null}
                </div>

                <div className="sess-chart-card">
                  <WeeklyChart data={evolution} />
                </div>
              </div>

              <div className="sess-panel">
                <div className="sess-panel__head">
                  <div>
                    <span>Composição</span>
                    <h2>Resumo do período</h2>
                    <p>Distribuição consolidada dos atendimentos.</p>
                  </div>
                </div>

                <div className="sess-composition">
                  <div className="sess-composition__chart psy-chart-surface">
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: "Online", value: online },
                            { name: "Presencial", value: presencial },
                            { name: "Canceladas", value: canceladas },
                          ].filter((item) => item.value > 0)}
                          dataKey="value" nameKey="name" innerRadius={58} outerRadius={86}
                          paddingAngle={2} cornerRadius={5} stroke="#ffffff" strokeWidth={2}
                        >
                          {[
                            { name: "Online", value: online },
                            { name: "Presencial", value: presencial },
                            { name: "Canceladas", value: canceladas },
                          ].filter((item) => item.value > 0).map((item, index) => (
                            <Cell key={item.name} fill={[PSY_CHART.indigo, PSY_CHART.green, PSY_CHART.red][index]} />
                          ))}
                        </Pie>
                        <Tooltip content={<PsyChartTooltip />} />
                        <Legend verticalAlign="bottom" iconType="circle" iconSize={8} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="sess-composition__center">
                      <strong>{total}</strong><span>sessões</span>
                    </div>
                  </div>
                  <div className="sess-composition__metrics">
                    <div><span>Concluídas</span><strong>{concluidas}</strong></div>
                    <div><span>Pagas</span><strong>{pagas}</strong></div>
                    <div><span>Pendentes</span><strong>{pendentes}</strong></div>
                    <div><span>Taxa de pagamento</span><strong>{percentBR(taxaPagamento)}%</strong></div>
                  </div>
                  <button className="sess-btn sess-btn--ghost-dark" type="button" onClick={goSchedule}>
                    <CalendarDays size={15} /> Abrir agenda
                  </button>
                </div>
              </div>
            </section>

            {isPremium && insights.length ? (
              <section className="sess-panel sess-panel--insights">
                <div className="sess-panel__head">
                  <div>
                    <span>Insights</span>
                    <h2>Insights com IA</h2>
                    <p>Análise automática sobre o comportamento das sessões.</p>
                  </div>
                </div>

                <div className="sess-insights">
                  <InsightsWidget title="Análise de Sessões" insights={insights} />
                </div>
              </section>
            ) : null}

            <section className="sess-panel">
              <div className="sess-panel__head sess-panel__head--with-tools">
                <div>
                  <span>Histórico</span>
                  <h2>Sessões recentes</h2>
                  <p>Busque por paciente, tipo, status ou data.</p>
                </div>

                <div className="sess-counter">
                  {filteredSessions.length} de {recentSessions.length}
                </div>
              </div>

              <div className="sess-toolbar">
                <label className="sess-search">
                  <Search size={16} aria-hidden="true" />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Buscar sessão..."
                  />

                  {search ? (
                    <button type="button" onClick={() => setSearch("")} aria-label="Limpar busca">
                      <X size={14} />
                    </button>
                  ) : null}
                </label>

                <div className="sess-filter-group" aria-label="Filtro de sessões">
                  {SESSION_FILTERS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={filter === option.value ? "is-active" : ""}
                      onClick={() => setFilter(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <div className="sess-total-chip">
                  <span>Filtradas</span>
                  <strong>{filteredCounter.total}</strong>
                </div>
              </div>

              {filteredSessions.length === 0 ? (
                <div className="sess-empty">
                  Nenhuma sessão encontrada com os filtros atuais.
                </div>
              ) : (
                <div className="sess-list">
                  {filteredSessions.map((session) => (
                    <article className={`sess-row is-${session.tipoKey}`} key={session.id}>
                      <div className={`sess-row__icon is-${session.tipoKey}`} aria-hidden="true">
                        {React.createElement(getTypeIcon(session.tipo), { size: 16, strokeWidth: 1.9 })}
                      </div>

                      <div className="sess-row__content">
                        <strong>{session.cliente || "Paciente"}</strong>
                        <span>
                          {formatDateBR(session.data)}
                          {session.status ? ` • ${session.status}` : ""}
                          {session.origem ? ` • ${session.origem}` : ""}
                        </span>
                        <small className="sess-row__meta">
                          {session.pagamentoEfetuado ? "Paga" : "Pendente"}
                          {session.formaPagamento ? ` • ${session.formaPagamento}` : ""}
                        </small>
                      </div>

                      <div className="sess-row__right">
                        <div className="sess-row__right-top">
                          <div className={`sess-row__tag is-${session.tipoKey}`}>{session.tipo}</div>
                          <strong className="sess-row__money">{brl(session.valor)}</strong>
                        </div>
                        {["concluida", "cancelada"].includes(normalizeText(session.status)) ? (
                          <button type="button" className="sess-row__edit" onClick={() => { setStatusModal(session); setStatusReason(""); }}>
                            <Edit3 size={14} /> Corrigir status
                          </button>
                        ) : null}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>


            {statusModal ? (
              <div className="sess-modal-backdrop" role="presentation">
                <div className="sess-modal" role="dialog" aria-modal="true" aria-labelledby="sess-status-modal-title">
                  <div className="sess-modal__head">
                    <div><span>Correção de registro</span><h2 id="sess-status-modal-title">Corrigir status da sessão</h2></div>
                    <button type="button" className="sess-icon-btn sess-icon-btn--light" onClick={() => !statusSaving && setStatusModal(null)} aria-label="Fechar"><X size={18} /></button>
                  </div>
                  <div className="sess-modal__status-grid">
                    <div><span>Status atual</span><strong>{normalizeText(statusModal.status) === "cancelada" ? "Cancelada" : "Concluída"}</strong></div>
                    <div><span>Novo status</span><strong>{normalizeText(statusModal.status) === "cancelada" ? "Concluída" : "Cancelada"}</strong></div>
                  </div>
                  <label className="sess-modal__field">
                    <span>Motivo da correção</span>
                    <textarea value={statusReason} onChange={(event) => setStatusReason(event.target.value)} placeholder="Ex.: sessão marcada como concluída por engano." rows={4} maxLength={500} autoFocus />
                  </label>
                  <div className="sess-modal__notice">A correção fica registrada no histórico. Pagamentos e receitas já existentes não são desfeitos automaticamente; confira o financeiro antes de confirmar.</div>
                  <div className="sess-modal__actions">
                    <button type="button" className="sess-btn sess-btn--ghost-dark" onClick={() => setStatusModal(null)} disabled={statusSaving}>Cancelar</button>
                    <button type="button" className="sess-btn sess-btn--primary" onClick={correctSessionStatus} disabled={statusSaving}>{statusSaving ? "Salvando..." : "Confirmar correção"}</button>
                  </div>
                </div>
              </div>
            ) : null}

            <section className="sess-bottom-actions">
              <button className="sess-btn sess-btn--ghost-dark" type="button" onClick={goBack}>
                Voltar aos relatórios
              </button>

              <button className="sess-btn sess-btn--primary" type="button" onClick={goSchedule}>
                Abrir agenda
              </button>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

